import React, { useState, useEffect, useRef } from 'react';
import { useAuth, useTenantsState } from '../auth/workosAuth';
import { config } from '../constants';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { Helmet } from 'react-helmet-async';
import axios from '../utils/axios';
import datajoltIcon from '../assets/img/icons/datajolt-icon.svg';
import DeleteSandboxModal from '../components/Modals/DeleteSandbox';
import StopSandboxModal from '../components/Modals/StopSandbox';
import { useEditorV2 } from '../contexts/EditorV2Context';
import ScheduleModal from '../components/ScheduleModal';
import PullRequestModalV2 from '../components/PullRequestModalV2';
import { CSSTransition } from 'react-transition-group';
import Table from '../components/global/Table/index.js';

export default function Code() {
  const { user } = useAuth();
  const { tenants } = useTenantsState();
  const activeTenant = tenants.find((t) => t.tenantId === user?.tenantId);
  const customerName = activeTenant?.name;
  const [sandboxFromUrl] = useState(() => new URLSearchParams(window.location.search).get('sandbox'));
  const [isNewFromUrl] = useState(() => new URLSearchParams(window.location.search).get('new') === '1');

  // Inject shimmer animation styles (light mode version)
  useEffect(() => {
    const styleId = 'shimmer-animation-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.4;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // isDeploying is in context so Navbar can show "← Environments" button during deployment
  const [deploymentStatus, setDeploymentStatus] = useState('Initializing...');
  const [deploymentProgress, setDeploymentProgress] = useState(0);
  const [error, setError] = useState(null);
  const [resumeError, setResumeError] = useState(false);
  const [sandboxes, setSandboxes] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoadingSandboxes, setIsLoadingSandboxes] = useState(true);
  const [operationError, setOperationError] = useState(null);
  const [editingSandboxId, setEditingSandboxId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);

  // Modal state
  const [deleteModalShown, setDeleteModalShown] = useState(false);
  const [stopModalShown, setStopModalShown] = useState(false);
  const [selectedSandbox, setSelectedSandbox] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);

  // VS Code iframe state - from context so Navbar can access it
  const { activeVSCode, setActiveVSCode, isDeploying, setIsDeploying, isScheduleModalShown, setIsScheduleModalShown, isPullRequestModalV2Shown, setIsPullRequestModalV2Shown, sandboxRefreshTrigger } = useEditorV2();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const iframeLoadTimeout = useRef(null);

  // When opened via /code?sandbox=ID or /code?new=1, show deploying screen immediately (no table flash)
  useEffect(() => {
    if (sandboxFromUrl || isNewFromUrl) {
      setIsDeploying(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set a loading timeout when iframe URL changes — show error if it doesn't load in 20s
  useEffect(() => {
    if (activeVSCode?.url) {
      setIframeError(false);
      if (iframeLoadTimeout.current) clearTimeout(iframeLoadTimeout.current);
      iframeLoadTimeout.current = setTimeout(() => {
        if (!iframeLoaded) {
          setIframeError(true);
          setIsDeploying(false);
        }
      }, 20000);
      return () => {
        if (iframeLoadTimeout.current) clearTimeout(iframeLoadTimeout.current);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVSCode?.url]);

  // ScheduleModal data state
  const [dataWarehouses, setDataWarehouses] = useState([]);
  const [dbtProfiles, setDbtProfiles] = useState([]);
  const [selectedDbtProfile, setSelectedDbtProfile] = useState(null);
  const [queueOptions, setQueueOptions] = useState([]);
  const [runningDbtTest, setRunningDbtTest] = useState(false);
  const [dbtLogs, setDbtLogs] = useState({ content: '', version: 0 });
  const [dbtTestStatus, setDbtTestStatus] = useState(null);
  const [testedCommandLineage, setTestedCommandLineage] = useState(null);

  // Separate DBT test state for PullRequestModalV2 (independent from ScheduleModal)
  const [prRunningDbtTest, setPrRunningDbtTest] = useState(false);
  const [prDbtLogs, setPrDbtLogs] = useState({ content: '', version: 0 });
  const [prDbtTestStatus, setPrDbtTestStatus] = useState(null);
  const [lineageStatus, setLineageStatus] = useState(null);
  const [runningLineage, setRunningLineage] = useState(false);
  const [skips, setSkips] = useState([]);

  // Track if sandbox fetch has been initiated to prevent duplicates
  const fetchInitiated = useRef(false);
  const openSandboxInProgress = useRef(false);

  useEffect(() => {
    // Fetch sandboxes once per page load (skip if opening a specific sandbox from URL)
    if (!fetchInitiated.current && customerName && !sandboxFromUrl && !isNewFromUrl) {
      fetchInitiated.current = true;
      fetchSandboxes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName]);

  // Auto-open sandbox from query param (for multi-tab support)
  const queryParamHandled = useRef(false);
  useEffect(() => {
    if (!queryParamHandled.current && sandboxFromUrl && customerName) {
      queryParamHandled.current = true;
      openSandbox(sandboxFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName]);

  // Auto-deploy when opened via /code?new=1
  const newParamHandled = useRef(false);
  useEffect(() => {
    if (!newParamHandled.current && isNewFromUrl && customerName) {
      newParamHandled.current = true;
      setIsCreatingNew(true);
      deployToDaytona();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerName]);

  // Refresh sandboxes when triggered from navbar "Back to Environments" button
  useEffect(() => {
    if (sandboxRefreshTrigger > 0) {
      fetchSandboxes();
    }
  }, [sandboxRefreshTrigger]);

  // Reset iframe loaded state when VSCode URL changes (for navigation back to page)
  useEffect(() => {
    if (activeVSCode) {
      setIframeLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVSCode?.url]);

  // Auto-open IDE if one is marked for auto-open
  const autoOpenTriggered = useRef(false);
  useEffect(() => {
    if (!autoOpenTriggered.current && sandboxes.length > 0 && !isLoadingSandboxes) {
      const autoOpenSandbox = sandboxes.find(s => s.auto_open && s.created_by === user?.email);
      if (autoOpenSandbox) {
        autoOpenTriggered.current = true;
        openSandbox(autoOpenSandbox.sandbox_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sandboxes, isLoadingSandboxes]);

  // Safety: Reset any stuck modal states on component mount
  useEffect(() => {
    setDeleteModalShown(false);
    setStopModalShown(false);
    setSelectedSandbox(null);
  }, []);

  // Safety: Add Escape key handler to close stuck modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setDeleteModalShown(false);
        setStopModalShown(false);
        setSelectedSandbox(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Animate progress to 100% when activeVSCode is set (iframe loading phase)
  useEffect(() => {
    if (activeVSCode && isDeploying && deploymentProgress < 100) {
      // Animate from current progress to 100% over 2.5 seconds
      const duration = 2500;
      const interval = 50;
      const remaining = 100 - deploymentProgress;
      const increment = remaining / (duration / interval);

      const timer = setInterval(() => {
        setDeploymentProgress(prev => {
          const next = prev + increment;
          if (next >= 100) {
            clearInterval(timer);
            return 100;
          }
          return next;
        });
      }, interval);

      return () => clearInterval(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVSCode?.url]); // Only trigger when activeVSCode URL changes

  // Fetch ScheduleModal data when modal opens (also needed for PullRequestModalV2)
  useEffect(() => {
    if ((isScheduleModalShown || isPullRequestModalV2Shown) && customerName) {
      fetchScheduleModalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScheduleModalShown, isPullRequestModalV2Shown, customerName]);

  const fetchScheduleModalData = async () => {
    try {
      const localUrl = "http://127.0.0.1:5000";
      const isLocalhost = config.url.SQL_EDITOR_URL === localUrl;
      const sqlEditorUrl = isLocalhost
        ? localUrl
        : `${config.url.SQL_EDITOR_URL}/${customerName}`;
      if (!sqlEditorUrl || (!isLocalhost && !customerName)) return;

      const response = await axios.get(`${sqlEditorUrl}/api/sql/get_selectables`);
      const selectables = response.data;

      // Filter dataWarehouses
      const warehouses = selectables
        .filter(item => item.type === "compute_wh")
        .map(item => ({ id: item.value, name: item.value }));
      setDataWarehouses(warehouses);

      // Filter dbtProfiles
      const profiles = selectables
        .filter(item => item.type === "dbt_profile")
        .map(item => item.value);
      setDbtProfiles(profiles);
      if (profiles.length > 0) {
        const saved = localStorage.getItem('selectedDbtProfile');
        setSelectedDbtProfile(saved && profiles.includes(saved) ? saved : profiles[0]);
      }

      // Filter queueOptions
      const queues = selectables
        .filter(item => item.type === "airflow_queue")
        .map(item => ({ id: item.value, name: item.value }));
      setQueueOptions(queues);
    } catch (err) {
      console.error('Failed to fetch schedule modal data:', err);
      // Fallback to empty defaults (matches v1 behavior)
      setDataWarehouses([]);
      setDbtProfiles([]);
      setQueueOptions([]);
    }
  };

  // Extract lineage fetch to separate function for reuse
  const fetchLineage = async (dbtUrl, timeStamp, command) => {
    setRunningLineage(true);
    try {
      const response = await axios.post(`${dbtUrl}/tested_dag_lineage`, {
        time_stamp: timeStamp,
        dbt_command: command,
      });
      setTestedCommandLineage(response.data["lineage"] || null);
      setLineageStatus('success');
      setSkips(response.data["skips"] || []);
    } catch (err) {
      console.error('Failed to fetch lineage:', err);
      setLineageStatus('failed');
    }
    setRunningLineage(false);
  };

  const testDbtModel = (command) => {
    const localUrl = "http://127.0.0.1:5000";
    const isLocalhost = config.url.DBT_URL === localUrl;
    const dbtUrl = isLocalhost ? localUrl : `${config.url.DBT_URL}/${customerName}`;
    if (!dbtUrl || (!isLocalhost && !customerName)) return;

    setRunningDbtTest(true);
    setDbtLogs(prev => ({ content: '[API] Testing your model...', version: prev.version + 1 }));
    setDbtTestStatus(null);
    setLineageStatus(null);
    setTestedCommandLineage(null);

    const timeStamp = Math.floor(Date.now() / 1000);

    axios.post(`${dbtUrl}/dbt_custom_command`, {
      dbt_branch: 'main',
      dbt_cli_env: selectedDbtProfile,
      dbt_command: command,
      get_lineage: true,
      time_stamp: timeStamp
    }, {
      onDownloadProgress: (progressEvent) => {
        // Use event.target instead of event.currentTarget (currentTarget becomes null after first few events)
        const api_logs = progressEvent.event?.target?.response;
        if (!api_logs) return;

        setDbtLogs(prev => ({ content: api_logs, version: prev.version + 1 }));

        // Check if finished (same logic as v1 - check for completion OR error)
        if (api_logs.includes('DBT run finished') || api_logs.includes('[ERROR]') || api_logs.includes('[API] Encountered an error:')) {
          setRunningDbtTest(false);

          if (api_logs.includes('DBT run finished successfully.')) {
            setDbtTestStatus('success');
            fetchLineage(dbtUrl, timeStamp, command);
          } else {
            setDbtTestStatus('failed');
          }
        }
      }
    }).catch((err) => {
      console.error('dbt test failed:', err);
      setDbtTestStatus('failed');
      setRunningDbtTest(false);
    });
  };

  // Separate DBT test function for PullRequestModalV2 (independent from ScheduleModal)
  const testDbtModelForPR = (command, branch) => {
    const localUrl = "http://127.0.0.1:5000";
    const isLocalhost = config.url.DBT_URL === localUrl;
    const dbtUrl = isLocalhost ? localUrl : `${config.url.DBT_URL}/${customerName}`;
    if (!dbtUrl || (!isLocalhost && !customerName)) return;

    setPrRunningDbtTest(true);
    setPrDbtLogs(prev => ({ content: '[API] Testing your branch...', version: prev.version + 1 }));
    setPrDbtTestStatus(null);

    axios.post(`${dbtUrl}/dbt_custom_command`, {
      dbt_branch: branch || 'main',
      dbt_cli_env: selectedDbtProfile,
      dbt_command: command,
      get_lineage: false,
      time_stamp: Math.floor(Date.now() / 1000)
    }, {
      onDownloadProgress: (progressEvent) => {
        const api_logs = progressEvent.event?.target?.response;
        if (!api_logs) return;

        setPrDbtLogs(prev => ({ content: api_logs, version: prev.version + 1 }));

        // Check if finished
        if (api_logs.includes('DBT run finished') || api_logs.includes('[ERROR]') || api_logs.includes('[API] Encountered an error:')) {
          setPrRunningDbtTest(false);

          if (api_logs.includes('DBT run finished successfully.')) {
            setPrDbtTestStatus('success');
          } else {
            setPrDbtTestStatus('failed');
          }
        }
      }
    }).catch((err) => {
      console.error('PR dbt test failed:', err);
      setPrDbtTestStatus('failed');
      setPrRunningDbtTest(false);
    });
  };

  const fetchSandboxes = async (silent = false) => {
    try {
      if (!silent) {
        setIsLoadingSandboxes(true);
      } else {
        setIsRefetching(true);
        // Safety timeout: force reset isRefetching after 10 seconds to prevent stuck state
        setTimeout(() => setIsRefetching(false), 10000);
      }
      const { data } = await axios.get(`${config.url.AI_URL}/api/ai/sandboxes`);
      setSandboxes(data.sandboxes || []);
      setIsAdmin(data.is_admin || false);
      if (!silent) {
        setIsLoadingSandboxes(false);
      } else {
        setIsRefetching(false);
      }
    } catch (err) {
      console.error('Error fetching sandboxes:', err);
      setError(err?.message || 'Failed to load environments');
      if (!silent) {
        setIsLoadingSandboxes(false);
      } else {
        setIsRefetching(false);
      }
    }
  };

  const openSandbox = async (sandboxId) => {
    if (openSandboxInProgress.current) return;
    openSandboxInProgress.current = true;
    let progressInterval = null;
    try {
      setIsCreatingNew(false);
      setIsDeploying(true);
      setDeploymentStatus('Resuming your environment...');
      setDeploymentProgress(10);

      // Start progress animation that runs DURING the API call
      progressInterval = setInterval(() => {
        setDeploymentProgress(prev => {
          // Gradually increase progress up to 90% while waiting
          if (prev < 90) {
            return prev + 2; // Increase by 2% every interval
          }
          return prev;
        });
      }, 1000); // Update every 1 second

      const { data } = await axios.post(`${config.url.AI_URL}/api/ai/sandbox/resume`, {
        sandbox_id: sandboxId
      });

      // Stop the progress animation
      clearInterval(progressInterval);
      progressInterval = null;

      if (data.success) {
        // Fast path: Sandbox already running - open immediately
        if (data.was_already_running) {
          setDeploymentProgress(100);
          setDeploymentStatus('Opening IDE...');

          // Embed VS Code iframe instead of redirecting
          // Keep isDeploying=true - iframe onLoad will set it to false
          setActiveVSCode({
            url: `https://code.datajolt.io/?token=${data.proxy_token}&sandbox=${sandboxId}&folder=${data.folder_path}`,
            name: data.sandbox_name || 'Environment'
          });
          return;
        }

        // Slow path: Sandbox was stopped and just resumed
        // Backend already did all the work (start + VPN + get URL)
        setDeploymentProgress(100);
        setDeploymentStatus('Environment ready! Opening IDE...');

        // Brief delay to show success message, then embed VS Code
        // Keep isDeploying=true - iframe onLoad will set it to false
        setTimeout(() => {
          setActiveVSCode({
            url: `https://code.datajolt.io/?token=${data.proxy_token}&sandbox=${sandboxId}&folder=${data.folder_path}`,
            name: data.sandbox_name || 'Environment'
          });
        }, 1000); // Brief delay to show success message
      } else {
        throw new Error(data.error || 'Failed to resume environment');
      }
    } catch (err) {
      // Clean up progress interval on error
      if (progressInterval) {
        clearInterval(progressInterval);
      }

      console.error('Resume error:', err);
      setResumeError(true);
      openSandboxInProgress.current = false;
      setTimeout(() => {
        window.location.href = '/code';
      }, 3000);
    }
  };

  const createNewEnvironment = (newTab = false) => {
    if (newTab) {
      window.open('/code?new=1', '_blank');
    } else {
      window.location.href = '/code?new=1';
    }
  };

  const deployToDaytona = async () => {
    let isComplete = false;

    try {
      // Simulate progress steps (5 seconds each)
      setDeploymentProgress(5);
      setDeploymentStatus('Creating sandbox — IDE will open when ready...');

      setTimeout(() => {
        if (!isComplete) {
          setDeploymentProgress(20);
          setDeploymentStatus('Setting up environment...');
        }
      }, 5000);

      setTimeout(() => {
        if (!isComplete) {
          setDeploymentProgress(35);
          setDeploymentStatus('Cloning your git repository...');
        }
      }, 10000);

      setTimeout(() => {
        if (!isComplete) {
          setDeploymentProgress(50);
          setDeploymentStatus('Configuring VS Code...');
        }
      }, 15000);

      setTimeout(() => {
        if (!isComplete) {
          setDeploymentProgress(65);
          setDeploymentStatus('Installing extensions...');
        }
      }, 20000);

      setTimeout(() => {
        if (!isComplete) {
          setDeploymentProgress(80);
          setDeploymentStatus('Installing dbt dependencies...');
        }
      }, 25000);

      setTimeout(() => {
        if (!isComplete) {
          setDeploymentProgress(90);
          setDeploymentStatus('Starting code server...');
        }
      }, 30000);

      const { data } = await axios.post(`${config.url.AI_URL}/api/ai/deploy-daytona`, {
        customer_name: customerName
      });

      if (data.success) {
        isComplete = true;
        setDeploymentProgress(100);
        setDeploymentStatus('Deployment successful! Opening IDE...');
        setTimeout(() => {
          setActiveVSCode({
            url: `https://code.datajolt.io/?token=${data.proxy_token}&sandbox=${data.sandbox_id}&folder=${data.folder_path}`,
            name: data.sandbox_name || 'Environment'
          });
        }, 1000);
      } else {
        throw new Error(data.error || 'Deployment failed');
      }
    } catch (err) {
      isComplete = true;
      console.error('Deployment error:', err);
      // The axios interceptor rejects with error.response.data directly
      // So err is already the response data, not the full error object
      let errorMessage;

      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err?.error) {
        errorMessage = err.error;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (typeof err === 'object') {
        errorMessage = JSON.stringify(err);
      } else {
        errorMessage = 'Deployment failed with unknown error';
      }

      // Use operationError to show error without hiding sandbox list
      setOperationError(errorMessage);
      setDeploymentStatus('Deployment failed');
      setIsDeploying(false);  // Return to list view
    }
  };

  const deleteSandbox = async (sandbox) => {
    try {
      setIsDeleting(true);
      setOperationError(null);
      const { data } = await axios.delete(`${config.url.AI_URL}/api/ai/sandbox/delete`, {
        data: { sandbox_id: sandbox.sandbox_id }
      });

      if (data.success) {
        // Refresh sandbox list (silent - no loading screen)
        await fetchSandboxes(true);
        setDeleteModalShown(false);
        setSelectedSandbox(null);
      } else {
        setOperationError(data.error || 'Failed to delete environment');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setOperationError(err?.error || err?.message || 'Failed to delete environment');
    } finally {
      setIsDeleting(false);
      // Safety: Always reset modal state even on error to prevent stuck UI
      setDeleteModalShown(false);
      setSelectedSandbox(null);
    }
  };

  const stopSandbox = async (sandbox) => {
    try {
      setIsStopping(true);
      setOperationError(null);
      const { data } = await axios.post(`${config.url.AI_URL}/api/ai/sandbox/stop`, {
        sandbox_id: sandbox.sandbox_id
      });

      if (data.success) {
        // Refresh sandbox list to show updated state (silent - no loading screen)
        await fetchSandboxes(true);
        setStopModalShown(false);
        setSelectedSandbox(null);
      } else {
        setOperationError(data.error || 'Failed to stop environment');
      }
    } catch (err) {
      console.error('Stop error:', err);
      setOperationError(err?.error || err?.message || 'Failed to stop environment');
    } finally {
      setIsStopping(false);
      // Safety: Always reset modal state even on error to prevent stuck UI
      setStopModalShown(false);
      setSelectedSandbox(null);
    }
  };

  const startRename = (sandboxId, currentName) => {
    setEditingSandboxId(sandboxId);
    setEditingName(currentName);
  };

  const saveName = async (sandboxId, nameOverride = null) => {
    const nameToSave = nameOverride || editingName.trim();
    if (!nameToSave) {
      setOperationError('Environment name cannot be empty');
      return;
    }

    try {
      setOperationError(null);
      const { data } = await axios.put(`${config.url.AI_URL}/api/ai/sandbox/rename`, {
        sandbox_id: sandboxId,
        sandbox_name: nameToSave
      });

      if (data.success) {
        // Refresh sandbox list (silent - no loading screen)
        await fetchSandboxes(true);
        // Clear editing state (if not already cleared by Enter key)
        setEditingSandboxId(null);
        setEditingName('');
      } else {
        setOperationError(data.error || 'Failed to rename environment');
      }
    } catch (err) {
      console.error('Rename error:', err);
      setOperationError(err?.error || err?.message || 'Failed to rename environment');
    }
  };

  const cancelRename = () => {
    setEditingSandboxId(null);
    setEditingName('');
  };

  const toggleAutoOpen = async (sandboxId, currentValue) => {
    const newValue = !currentValue;

    // Prevent auto-open useEffect from triggering when user manually enables auto_open
    if (newValue) {
      autoOpenTriggered.current = true;
    }

    // Optimistic update - immediately update UI
    setSandboxes(prev => prev.map(s => ({
      ...s,
      auto_open: s.sandbox_id === sandboxId ? newValue : (newValue ? false : s.auto_open)
    })));

    try {
      setOperationError(null);
      await axios.put(`${config.url.AI_URL}/api/ai/sandbox/auto-open`, {
        sandbox_id: sandboxId,
        auto_open: newValue
      });
    } catch (err) {
      console.error('Auto-open toggle error:', err);
      setOperationError(err?.error || err?.message || 'Failed to update auto-open');
      // Revert on error
      await fetchSandboxes(true);
    }
  };

  if (error) {
    return (
      <>
        <Helmet title="Datajolt | Code" />
        <div style={{
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          color: '#293042',
          fontFamily: "'Poppins', sans-serif",
        }}>
          <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>Deployment Failed</h2>
        <p style={{ marginBottom: '20px', color: '#6c757d' }}>{error}</p>
        <button
          onClick={() => { window.location.href = '/code'; }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3651FE',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(54, 81, 254, 0.3)',
            fontFamily: "'Poppins', sans-serif",
            fontWeight: '500',
          }}
        >
          Back to Environments
        </button>
        </div>
      </>
    );
  }

  // Show embedded VS Code when a sandbox is active
  // Loading overlay shows on top when isDeploying is true (iframe loading in background)
  if (activeVSCode) {
    return (
      <>
        <Helmet title={activeVSCode.name && activeVSCode.name !== 'Environment' ? `${activeVSCode.name} | Datajolt Code` : 'Datajolt | Code'} />
        <div style={{
          position: 'fixed',
          top: '0px',
          left: '60px',
          right: 0,
          bottom: 0,
          margin: 0,
          padding: 0,
          overflow: 'hidden',
        }}>
          {/* Loading overlay - shown while iframe is loading */}
          {isDeploying && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#f8f9fa',
              zIndex: 10,
            }}>
              {/* Main content - centered */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CircularProgress
                  size={50}
                  thickness={4}
                  sx={{
                    color: '#3651FE',
                    marginBottom: '30px',
                  }}
                />
                <h2 style={{
                  marginBottom: '10px',
                  color: '#293042',
                  background: 'linear-gradient(90deg, rgba(41,48,66,0.3) 0%, rgba(41,48,66,1) 50%, rgba(41,48,66,0.3) 100%)',
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: 'shimmer 2s linear infinite',
                  fontFamily: "'Poppins', sans-serif",
                }}>
                  Loading coding environment for {customerName}...
                </h2>
                <p style={{
                  color: '#6c757d',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontFamily: "'Poppins', sans-serif",
                }}>
                  {(deploymentStatus.includes('successful') || deploymentStatus.includes('Opening')) && (
                    <CheckCircleIcon style={{ color: '#28a745', fontSize: '20px' }} />
                  )}
                  {deploymentStatus}
                </p>

                {/* Progress Bar */}
                <div style={{
                  marginTop: '30px',
                  width: '400px',
                  height: '4px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '2px',
                  overflow: 'hidden',
                  position: 'relative',
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${deploymentProgress}%`,
                    background: 'linear-gradient(90deg, #3651FE 0%, #5a75ff 100%)',
                    borderRadius: '2px',
                    transition: 'width 0.5s ease-in-out',
                  }} />
                </div>
                <p style={{
                  marginTop: '10px',
                  color: '#3651FE',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                }}>
                  {Math.round(deploymentProgress)}%
                </p>
              </div>
            </div>
          )}

          {/* Simple spinner shown when returning to page (iframe loading but not deploying) */}
          {!isDeploying && !iframeLoaded && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8f9fa',
              zIndex: 5,
            }}>
              <CircularProgress size={40} sx={{ color: '#3651FE' }} />
            </div>
          )}

          {/* Iframe error state */}
          {iframeError && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8f9fa',
              zIndex: 10,
              gap: '16px',
            }}>
              <p style={{ color: '#666', fontSize: '14px' }}>Environment is loading. This can take a few seconds...</p>
              <button
                onClick={() => {
                  setIframeError(false);
                  setIframeLoaded(false);
                  setActiveVSCode(prev => ({ ...prev, url: prev.url + '&retry=' + Date.now() }));
                }}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#3651FE',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* Iframe - always rendered, hidden behind overlay when loading */}
          <iframe
            src={activeVSCode.url}
            onLoad={() => {
              // Clear the loading timeout since iframe loaded
              if (iframeLoadTimeout.current) {
                clearTimeout(iframeLoadTimeout.current);
                iframeLoadTimeout.current = null;
              }
              // Delay to allow VS Code to fully render inside iframe
              setTimeout(() => {
                setIframeLoaded(true);
                setIsDeploying(false);
              }, 1500);
            }}
            onError={() => {
              if (iframeLoadTimeout.current) {
                clearTimeout(iframeLoadTimeout.current);
                iframeLoadTimeout.current = null;
              }
              setIframeError(true);
              setIsDeploying(false);
            }}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
              margin: 0,
              padding: 0,
              opacity: isDeploying ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out',
            }}
            title="VS Code Editor"
            allow="clipboard-read; clipboard-write"
          />
        </div>

        {/* ScheduleModal for Create DAG sidebar when VS Code is active */}
        <CSSTransition
          in={isScheduleModalShown}
          classNames="right-slide"
          timeout={300}
          unmountOnExit
        >
          <ScheduleModal
            fromV2={true}
            isShown={isScheduleModalShown}
            setIsShown={setIsScheduleModalShown}
            testModel={testDbtModel}
            lineageData={{ initial_workflow_chain: testedCommandLineage, workflow_name: '' }}
            testData={{
              running_dbt: runningDbtTest,
              dbt_logs: dbtLogs.content,
              dbt_test_status: dbtTestStatus,
              running_lineage: runningLineage,
              lineage_status: lineageStatus
            }}
            skips_={skips}
            dataWarehouses={dataWarehouses}
            initialComputeWarehouse={localStorage.getItem('selectedDatawarehouse')}
            dbtProfile={selectedDbtProfile}
            setDbtProfile={setSelectedDbtProfile}
            dbtProfiles={dbtProfiles}
            queueOptions={queueOptions}
          />
        </CSSTransition>

        {/* PullRequestModalV2 for Create PR sidebar when VS Code is active */}
        <CSSTransition
          in={isPullRequestModalV2Shown}
          classNames="right-slide"
          timeout={300}
          unmountOnExit
        >
          <PullRequestModalV2
            isShown={isPullRequestModalV2Shown}
            setIsShown={setIsPullRequestModalV2Shown}
            activeTenant={customerName}
            test_dbt_model={testDbtModelForPR}
            dbt_test_status={prDbtTestStatus}
            dbt_logs={prDbtLogs.content}
            dbtProfile={selectedDbtProfile}
            dbtProfiles={dbtProfiles}
            setDbtProfile={setSelectedDbtProfile}
          />
        </CSSTransition>
      </>
    );
  }

  // Only show standalone loading screen during API call phase (before activeVSCode is set)
  // Once activeVSCode is set, the loading overlay in that block handles the loading state
  if (isDeploying && !activeVSCode) {
    return (
      <>
        <Helmet title="Datajolt | Code" />
        <div style={{
          width: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          color: '#293042',
          fontFamily: "'Poppins', sans-serif",
        }}>
          {resumeError ? (
            <LockOutlinedIcon sx={{ fontSize: 50, color: '#dc3545', marginBottom: '30px' }} />
          ) : (
            <CircularProgress
              size={50}
              thickness={4}
              sx={{
                color: '#3651FE',
                marginBottom: '30px',
              }}
            />
          )}
        <h2 style={{
          marginBottom: '10px',
          color: resumeError ? '#dc3545' : '#293042',
          ...(!resumeError && {
            background: 'linear-gradient(90deg, rgba(41,48,66,0.3) 0%, rgba(41,48,66,1) 50%, rgba(41,48,66,0.3) 100%)',
            backgroundSize: '200% 100%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'shimmer 2s linear infinite',
          }),
          fontFamily: "'Poppins', sans-serif",
        }}>
          {resumeError
            ? 'Environment not found or access denied'
            : customerName
              ? isCreatingNew
                ? `Creating new coding environment for ${customerName}...`
                : `Loading coding environment for ${customerName}...`
              : 'Checking permissions...'}
        </h2>
        <p style={{
          color: '#6c757d',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontFamily: "'Poppins', sans-serif",
        }}>
          {resumeError
            ? 'You are being redirected to your Code environment list automatically.'
            : <>
                {deploymentStatus.includes('successful') && (
                  <CheckCircleIcon style={{ color: '#28a745', fontSize: '20px' }} />
                )}
                {deploymentStatus}
              </>
          }
        </p>

        {!resumeError && (
          <>
            {/* Modern Progress Bar */}
            <div style={{
              marginTop: '30px',
              width: '400px',
              height: '4px',
              backgroundColor: '#e9ecef',
              borderRadius: '2px',
              overflow: 'hidden',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: `${deploymentProgress}%`,
                background: 'linear-gradient(90deg, #3651FE 0%, #5a75ff 100%)',
                borderRadius: '2px',
                transition: 'width 0.5s ease-in-out',
              }} />
            </div>
            {/* Progress percentage */}
            <p style={{
              marginTop: '10px',
              color: '#3651FE',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: "'Poppins', sans-serif",
            }}>
              {deploymentProgress}%
            </p>
          </>
        )}
        </div>

        {/* ScheduleModal for Create DAG sidebar during deployment */}
        <CSSTransition
          in={isScheduleModalShown}
          classNames="right-slide"
          timeout={300}
          unmountOnExit
        >
          <ScheduleModal
            fromV2={true}
            isShown={isScheduleModalShown}
            setIsShown={setIsScheduleModalShown}
            testModel={testDbtModel}
            lineageData={{ initial_workflow_chain: testedCommandLineage, workflow_name: '' }}
            testData={{
              running_dbt: runningDbtTest,
              dbt_logs: dbtLogs.content,
              dbt_test_status: dbtTestStatus,
              running_lineage: runningLineage,
              lineage_status: lineageStatus
            }}
            skips_={skips}
            dataWarehouses={dataWarehouses}
            initialComputeWarehouse={localStorage.getItem('selectedDatawarehouse')}
            dbtProfile={selectedDbtProfile}
            setDbtProfile={setSelectedDbtProfile}
            dbtProfiles={dbtProfiles}
            queueOptions={queueOptions}
          />
        </CSSTransition>

        {/* PullRequestModalV2 for Create PR sidebar during deployment */}
        <CSSTransition
          in={isPullRequestModalV2Shown}
          classNames="right-slide"
          timeout={300}
          unmountOnExit
        >
          <PullRequestModalV2
            isShown={isPullRequestModalV2Shown}
            setIsShown={setIsPullRequestModalV2Shown}
            activeTenant={customerName}
            test_dbt_model={testDbtModelForPR}
            dbt_test_status={prDbtTestStatus}
            dbt_logs={prDbtLogs.content}
            dbtProfile={selectedDbtProfile}
            dbtProfiles={dbtProfiles}
            setDbtProfile={setSelectedDbtProfile}
          />
        </CSSTransition>
      </>
    );
  }

  // Show sandbox list
  return (
    <>
      <Helmet title="Datajolt | Code" />
      <div className="container-fluid py-3" style={{
        backgroundColor: '#f8f9fa',
        color: '#293042',
        fontFamily: "'Poppins', sans-serif",
      }}>
        {/* Header with title and create button - aligned with Taps.js style */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          margin: '12px 8px',
          height: '45px',
        }}>
          <h4 style={{ marginBottom: 0 }}>
            <b style={{ color: '#090E47', marginRight: '10px' }}>
              Code Environments ({sandboxes.length})
            </b>
          </h4>
          <div style={{ display: 'flex', marginLeft: 'auto', alignItems: 'center', gap: 10 }}>
            <Tooltip title="Refresh Environments" placement="bottom">
              <IconButton
                onClick={() => fetchSandboxes()}
                disabled={isLoadingSandboxes}
                size="small"
                sx={{
                  padding: '4px',
                  '&:hover': { backgroundColor: 'rgba(44, 73, 252, 0.08)' }
                }}
              >
                {isLoadingSandboxes ? (
                  <CircularProgress size={16} sx={{ color: '#2c49fc' }} />
                ) : (
                  <RefreshIcon sx={{ fontSize: 20, color: '#6c757d' }} />
                )}
              </IconButton>
            </Tooltip>
            <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon style={{ fontSize: '16px' }} />}
                onClick={() => createNewEnvironment(false)}
                sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  height: '32px',
                  padding: '0 16px',
                  textTransform: 'none',
                  borderRadius: '6px 0 0 6px',
                  background: '#2D49FD',
                  border: '1px solid #2D49FD',
                  '&:hover': {
                    opacity: 0.9
                  }
                }}
              >
                New Environment
              </Button>
              <Tooltip
                placement="bottom"
                title="Open in new tab"
                arrow
                disableInteractive={true}
                slotProps={{
                  arrow: { sx: { color: "#546e7a" } },
                  tooltip: { sx: {
                    backgroundColor: "#546e7a",
                    fontSize: "11px",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                  }}
                }}
              >
                <button
                  onClick={() => createNewEnvironment(true)}
                  style={{
                    padding: '0 8px',
                    height: '32px',
                    backgroundColor: '#2D49FD',
                    color: 'white',
                    border: 'none',
                    borderLeft: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '0 6px 6px 0',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <OpenInNewIcon style={{ fontSize: '14px' }} />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        <hr style={{ margin: "0 8px", width: "100%" }} />
        <br />

        {/* Table using reusable Table component - aligned with Taps.js */}
        {(sandboxes.length > 0 || isLoadingSandboxes) && (
        <Table
          headings={[
            {
              columnName: 'name',
              isSortable: false,
              Children: ({ children }) => (
                <span className="d-flex align-items-center h-100">
                  Name
                  {children}
                </span>
              ),
            },
            {
              columnName: 'status',
              isSortable: false,
              Children: ({ children }) => (
                <span className="d-flex align-items-center h-100">
                  Status
                  {children}
                </span>
              ),
            },
            {
              columnName: 'auto_open',
              isSortable: false,
              Children: ({ children }) => (
                <span className="d-flex align-items-center h-100">
                  Auto Open
                  {children}
                </span>
              ),
            },
            {
              columnName: 'last_accessed',
              isSortable: false,
              Children: ({ children }) => (
                <span className="d-flex align-items-center h-100">
                  Last Accessed
                  {children}
                </span>
              ),
            },
            ...(isAdmin ? [{
              columnName: 'created_by',
              isSortable: false,
              Children: ({ children }) => (
                <span className="d-flex align-items-center h-100">
                  Created By
                  {children}
                </span>
              ),
            }] : []),
            {
              width: '15%',
              columnName: 'actions',
              isSortable: false,
              Children: () => 'Actions',
            },
          ]}
          rows={sandboxes.map((sandbox) => ({
            key: sandbox.sandbox_id,
            columns: [
              {
                type: 'component',
                component: editingSandboxId === sandbox.sandbox_id ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#293042',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        padding: '6px 10px',
                        fontSize: '13px',
                        flex: 1,
                        fontFamily: "'Poppins', sans-serif",
                        outline: 'none',
                      }}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const nameToSave = editingName.trim();
                          setEditingSandboxId(null);
                          setEditingName('');
                          if (nameToSave) saveName(sandbox.sandbox_id, nameToSave);
                        }
                        if (e.key === 'Escape') cancelRename();
                      }}
                    />
                    <button
                      onClick={() => saveName(sandbox.sandbox_id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelRename}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'transparent',
                        color: '#6c757d',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <span style={{ fontWeight: '500', color: '#293042', fontSize: '10px' }}>
                    {sandbox.sandbox_name}
                  </span>
                ),
              },
              {
                type: 'component',
                component: (
                  <span style={{
                    fontSize: '10px',
                    fontWeight: '500',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    backgroundColor: sandbox.status === 'running'
                      ? 'rgba(40, 167, 69, 0.1)'
                      : 'rgba(255, 193, 7, 0.15)',
                    color: sandbox.status === 'running'
                      ? '#28a745'
                      : '#e67c00',
                    display: 'inline-block',
                  }}>
                    <span style={{
                      display: 'inline-block',
                      animation: sandbox.status === 'running' ? 'pulse 2s ease-in-out infinite' : 'none',
                    }}>●</span> {sandbox.status === 'running' ? 'Running' : 'Stopped'}
                  </span>
                ),
              },
              {
                type: 'component',
                component: (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleAutoOpen(sandbox.sandbox_id, sandbox.auto_open);
                    }}
                    disabled={editingSandboxId !== null}
                    style={{
                      width: '36px',
                      height: '20px',
                      borderRadius: '10px',
                      border: 'none',
                      backgroundColor: sandbox.auto_open ? '#3651FE' : '#dee2e6',
                      cursor: editingSandboxId === null ? 'pointer' : 'not-allowed',
                      position: 'relative',
                      transition: 'background-color 0.2s',
                      opacity: editingSandboxId === null ? 1 : 0.5,
                    }}
                  >
                    <span style={{
                      position: 'absolute',
                      top: '2px',
                      left: sandbox.auto_open ? '18px' : '2px',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: '#ffffff',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }} />
                  </button>
                ),
              },
              {
                type: 'component',
                component: (
                  <span style={{ fontSize: '10px', color: '#6c757d' }}>
                    {sandbox.last_accessed
                      ? new Date(sandbox.last_accessed).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : '-'}
                  </span>
                ),
              },
              ...(isAdmin ? [{
                type: 'component',
                component: (
                  <span style={{ fontSize: '10px', color: '#6c757d' }}>
                    {sandbox.created_by || '-'}
                  </span>
                ),
              }] : []),
              {
                type: 'component',
                component: (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                    <Tooltip
                      placement="bottom"
                      title="Edit"
                      arrow
                      disableInteractive={true}
                      slotProps={{
                        arrow: { sx: { color: "#546e7a" } },
                        tooltip: { sx: {
                          backgroundColor: "#546e7a",
                          fontSize: "11px",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                        }}
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startRename(sandbox.sandbox_id, sandbox.sandbox_name);
                        }}
                        disabled={editingSandboxId !== null}
                        style={{
                          padding: '6px',
                          backgroundColor: 'transparent',
                          color: '#6c757d',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: editingSandboxId === null ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          opacity: editingSandboxId === null ? 1 : 0.4,
                        }}
                      >
                        <EditIcon style={{ fontSize: '16px' }} />
                      </button>
                    </Tooltip>
                    <Tooltip
                      placement="bottom"
                      title="Stop sandbox"
                      arrow
                      disableInteractive={true}
                      slotProps={{
                        arrow: { sx: { color: "#546e7a" } },
                        tooltip: { sx: {
                          backgroundColor: "#546e7a",
                          fontSize: "11px",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                        }}
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSandbox(sandbox);
                          setStopModalShown(true);
                        }}
                        disabled={editingSandboxId !== null}
                        style={{
                          padding: '6px',
                          backgroundColor: 'transparent',
                          color: '#6c757d',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: editingSandboxId === null ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          opacity: editingSandboxId === null ? 1 : 0.4,
                        }}
                      >
                        <StopIcon style={{ fontSize: '16px' }} />
                      </button>
                    </Tooltip>
                    <div style={{ display: 'flex', borderRadius: '4px', overflow: 'hidden', opacity: editingSandboxId === null ? 1 : 0.4 }}>
                      <Tooltip
                        placement="bottom"
                        title="Open IDE"
                        arrow
                        disableInteractive={true}
                        slotProps={{
                          arrow: { sx: { color: "#546e7a" } },
                          tooltip: { sx: {
                            backgroundColor: "#546e7a",
                            fontSize: "11px",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                          }}
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `/code?sandbox=${sandbox.sandbox_id}`;
                          }}
                          disabled={editingSandboxId !== null}
                          style={{
                            padding: '6px 8px',
                            backgroundColor: '#3651FE',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px 0 0 4px',
                            cursor: editingSandboxId === null ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <img
                            src={datajoltIcon}
                            alt="Datajolt"
                            style={{ width: '16px', height: '16px', objectFit: 'contain' }}
                          />
                        </button>
                      </Tooltip>
                      <Tooltip
                        placement="bottom"
                        title="Open in new tab"
                        arrow
                        disableInteractive={true}
                        slotProps={{
                          arrow: { sx: { color: "#546e7a" } },
                          tooltip: { sx: {
                            backgroundColor: "#546e7a",
                            fontSize: "11px",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                          }}
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/code?sandbox=${sandbox.sandbox_id}`, '_blank');
                          }}
                          disabled={editingSandboxId !== null}
                          style={{
                            padding: '6px 6px',
                            backgroundColor: '#3651FE',
                            color: 'white',
                            border: 'none',
                            borderLeft: '1px solid rgba(255,255,255,0.3)',
                            borderRadius: '0 4px 4px 0',
                            cursor: editingSandboxId === null ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <OpenInNewIcon style={{ fontSize: '12px' }} />
                        </button>
                      </Tooltip>
                    </div>
                    <Tooltip
                      placement="bottom"
                      title="Delete"
                      arrow
                      disableInteractive={true}
                      slotProps={{
                        arrow: { sx: { color: "#546e7a" } },
                        tooltip: { sx: {
                          backgroundColor: "#546e7a",
                          fontSize: "11px",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                        }}
                      }}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSandbox(sandbox);
                          setDeleteModalShown(true);
                        }}
                        disabled={editingSandboxId !== null}
                        style={{
                          padding: '6px',
                          backgroundColor: 'transparent',
                          color: '#dc3545',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: editingSandboxId === null ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          opacity: editingSandboxId === null ? 0.7 : 0.3,
                        }}
                      >
                        <DeleteIcon style={{ fontSize: '16px' }} />
                      </button>
                    </Tooltip>
                  </div>
                ),
              },
            ],
          }))}
          sortedColumn={{ column: '', isAsc: true }}
          onSortColumn={() => {}}
          isLoading={isLoadingSandboxes}
          loadingOptions={{ columns: 5, rows: 3 }}
        />
        )}

        {/* Error message */}
        {operationError && (
          <div style={{
            marginTop: '24px',
            padding: '12px 16px',
            backgroundColor: 'rgba(220, 53, 69, 0.1)',
            color: '#dc3545',
            border: '1px solid rgba(220, 53, 69, 0.3)',
            borderRadius: '6px',
            width: '100%',
            fontFamily: "'Poppins', sans-serif",
            fontSize: '13px',
          }}>
            {operationError}
          </div>
        )}

        {/* Empty state - only show when not loading and no sandboxes */}
        {sandboxes.length === 0 && !isLoadingSandboxes && (
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            padding: '60px 40px',
            textAlign: 'center',
            marginLeft: '8px',
            marginRight: '8px',
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px',
            }}>🚀</div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#293042',
              margin: '0 0 8px 0',
              fontFamily: "'Poppins', sans-serif",
            }}>No environments yet</h3>
            <p style={{
              fontSize: '14px',
              color: '#6c757d',
              margin: '0 0 24px 0',
              fontFamily: "'Poppins', sans-serif",
            }}>Create your first coding environment to get started</p>
            <div style={{ display: 'inline-flex', borderRadius: '6px', overflow: 'hidden' }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => createNewEnvironment(false)}
                sx={{
                  fontSize: '11px',
                  textTransform: 'none',
                  borderRadius: '6px 0 0 6px',
                }}
              >
                New Environment
              </Button>
              <Tooltip
                placement="bottom"
                title="Open in new tab"
                arrow
                disableInteractive={true}
                slotProps={{
                  arrow: { sx: { color: "#546e7a" } },
                  tooltip: { sx: {
                    backgroundColor: "#546e7a",
                    fontSize: "11px",
                    padding: "6px 12px",
                    borderRadius: "6px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                  }}
                }}
              >
                <button
                  onClick={() => createNewEnvironment(true)}
                  style={{
                    padding: '0 8px',
                    backgroundColor: 'transparent',
                    color: '#1976d2',
                    border: '1px solid rgba(25, 118, 210, 0.5)',
                    borderLeft: 'none',
                    borderRadius: '0 6px 6px 0',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <OpenInNewIcon style={{ fontSize: '14px' }} />
                </button>
              </Tooltip>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <DeleteSandboxModal
        isShown={deleteModalShown}
        onHide={() => setDeleteModalShown(false)}
        sandboxName={selectedSandbox?.sandbox_name}
        onConfirm={() => deleteSandbox(selectedSandbox)}
        isDeleting={isDeleting}
      />

      <StopSandboxModal
        isShown={stopModalShown}
        onHide={() => setStopModalShown(false)}
        sandboxName={selectedSandbox?.sandbox_name}
        onConfirm={() => stopSandbox(selectedSandbox)}
        isStopping={isStopping}
      />

      <CSSTransition
        in={isScheduleModalShown}
        classNames="right-slide"
        timeout={300}
        unmountOnExit
      >
        <ScheduleModal
          fromV2={true}
          isShown={isScheduleModalShown}
          setIsShown={setIsScheduleModalShown}
          testModel={testDbtModel}
          lineageData={{ initial_workflow_chain: testedCommandLineage, workflow_name: '' }}
          testData={{
            running_dbt: runningDbtTest,
            dbt_logs: dbtLogs.content,
            dbt_test_status: dbtTestStatus,
            running_lineage: runningLineage,
            lineage_status: lineageStatus
          }}
          skips_={skips}
          dataWarehouses={dataWarehouses}
          initialComputeWarehouse={localStorage.getItem('selectedDatawarehouse')}
          dbtProfile={selectedDbtProfile}
          setDbtProfile={setSelectedDbtProfile}
          dbtProfiles={dbtProfiles}
          queueOptions={queueOptions}
        />
      </CSSTransition>

      {/* PullRequestModalV2 for Create PR sidebar */}
      <CSSTransition
        in={isPullRequestModalV2Shown}
        classNames="right-slide"
        timeout={300}
        unmountOnExit
      >
        <PullRequestModalV2
          isShown={isPullRequestModalV2Shown}
          setIsShown={setIsPullRequestModalV2Shown}
          activeTenant={customerName}
          test_dbt_model={testDbtModelForPR}
          dbt_test_status={prDbtTestStatus}
          dbt_logs={prDbtLogs.content}
          dbtProfile={selectedDbtProfile}
          dbtProfiles={dbtProfiles}
          setDbtProfile={setSelectedDbtProfile}
        />
      </CSSTransition>
    </>
  );
}
